(() => {
  class ProductVariants {
    constructor(section) {
      this.section = section;
      this.variantSelect = section.querySelector('[data-variant-select]');
      this.variantPills = Array.from(section.querySelectorAll('[data-variant-pill]'));
      this.priceElement = section.querySelector('[data-product-price]');
      this.comparePriceElement = section.querySelector('[data-product-compare-price]');
      this.addButton = section.querySelector('[data-add-to-cart]');
      this.preorderNotice = section.querySelector('[data-preorder-notice]');
      this.preorderProperty = section.querySelector('[data-preorder-property]');
      this.preorderMessageProperty = section.querySelector('[data-preorder-message-property]');
      this.variantBadge = section.querySelector('[data-variant-badge]');

      this.pillHandlers = new Map();
      this.handleVariantChange = this.update.bind(this);

      this.bindEvents();
      this.update();
    }

    bindEvents() {
      this.variantPills.forEach((pill) => {
        const handler = () => this.selectPill(pill);
        this.pillHandlers.set(pill, handler);
        pill.addEventListener('click', handler);
      });

      this.variantSelect?.addEventListener('change', this.handleVariantChange);
    }

    selectPill(pill) {
      if (pill.disabled || !this.variantSelect) return;

      this.variantSelect.value = pill.dataset.variantId;
      this.variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    getSelectedState() {
      if (!this.variantSelect) return null;

      const selectedOption = this.variantSelect.options[this.variantSelect.selectedIndex];
      if (!selectedOption) return null;

      const price = Number(selectedOption.dataset.priceAmount || 0);
      const compareAtPrice = Number(selectedOption.dataset.compareAtPrice || 0);
      const available = selectedOption.dataset.available === 'true';
      const inventoryPolicy = selectedOption.dataset.inventoryPolicy;
      const preorderEnabled = selectedOption.dataset.preorderEnabled === 'true';
      const isPreorder = preorderEnabled && inventoryPolicy === 'continue';

      return {
        selectedOption,
        price,
        compareAtPrice,
        available,
        isPreorder,
      };
    }

    getAddButtonLabel() {
      const state = this.getSelectedState();

      if (!state) {
        return this.addButton?.disabled ? 'Sold out' : 'Add to cart';
      }

      if (!state.available) return 'Sold out';
      return state.isPreorder ? 'Pre-order' : 'Add to cart';
    }

    updateBadge(state) {
      if (!this.variantBadge) return;

      let badgeText = '';
      let badgeState = '';

      if (state.isPreorder) {
        badgeText = 'Pre-order';
        badgeState = 'preorder';
      } else if (!state.available) {
        badgeText = 'Out of stock';
        badgeState = 'out-of-stock';
      } else if (state.compareAtPrice > state.price) {
        badgeText = 'Sale';
        badgeState = 'sale';
      }

      this.variantBadge.textContent = badgeText;
      this.variantBadge.dataset.badgeState = badgeState;
      this.variantBadge.hidden = badgeText === '';
    }

    update() {
      if (!this.variantSelect || !this.priceElement || !this.addButton) return;

      const state = this.getSelectedState();
      if (!state) return;

      const { selectedOption, price, compareAtPrice, available, isPreorder } = state;

      if (selectedOption.dataset.price) {
        this.priceElement.textContent = selectedOption.dataset.price;
      }

      this.priceElement.classList.toggle('is-sale', compareAtPrice > price);

      if (this.comparePriceElement) {
        if (compareAtPrice > price) {
          this.comparePriceElement.textContent = selectedOption.dataset.compareAtPriceFormatted;
          this.comparePriceElement.hidden = false;
        } else {
          this.comparePriceElement.textContent = '';
          this.comparePriceElement.hidden = true;
        }
      }

      this.addButton.disabled = !available;
      this.addButton.textContent = this.getAddButtonLabel();

      if (this.preorderNotice) {
        this.preorderNotice.hidden = !isPreorder;
      }

      if (this.preorderProperty) {
        this.preorderProperty.disabled = !isPreorder;
      }

      if (this.preorderMessageProperty) {
        this.preorderMessageProperty.disabled = !isPreorder;
      }

      this.variantPills.forEach((pill) => {
        const isSelected = pill.dataset.variantId === selectedOption.value;
        pill.classList.toggle('is-active', isSelected);
        pill.setAttribute('aria-pressed', String(isSelected));
      });

      this.updateBadge(state);

      this.section.dispatchEvent(
        new CustomEvent('product:variant-change', {
          bubbles: true,
          detail: {
            variantId: selectedOption.value,
            price,
            compareAtPrice,
            available,
            isPreorder,
          },
        })
      );
    }

    destroy() {
      this.variantSelect?.removeEventListener('change', this.handleVariantChange);

      this.pillHandlers.forEach((handler, pill) => {
        pill.removeEventListener('click', handler);
      });

      this.pillHandlers.clear();
    }
  }

  window.ProductVariants = {
    create(section) {
      if (!section) return null;
      return new ProductVariants(section);
    },
  };
})();
